import { useMemo, useRef } from 'react';
import { removeGid } from './handleGid';
import { fileToDataURL } from './fileToDataURL';
import { createGroupCacheKey } from './createGroupCacheKey';
import { debounce } from '../../../base/global.js';
import { supportsWebP } from './webpDetection.js';

const imageType = supportsWebP() ? 'webp' : 'png';

const VITE_IMAGE_CDN_URL = import.meta.env.VITE_IMAGE_CDN_URL;
const PRODUCT_ID = window.personalisation.product.id;
const VIS_ENABLED = window.personalisation.visEnabled;

/**
 * @typedef {Object} FilteredSelections
 * @property {import('../context/personalisation-typedefs').Selections} selections
 * @property {string} productVariantId
 */

/** @typedef {import('../context/personalisation-typedefs').SelectionSlot} SelectionSlot */

/**
 * Composites an overlay image on top of a base image using a canvas,
 * positioned within the mask bounding box.
 * @param {string} baseDataUrl - The base image as a data URL
 * @param {string} overlayUrl - The overlay image URL
 * @param {{ x: number, y: number, scale: number } | null} positioning - Mask position (x/y as % from top-left center, scale as % of width)
 * @param {{ width: number, height: number } | null} maskDimensions - Natural mask image dimensions for aspect ratio
 * @returns {Promise<{dataUrl: string, blob: Blob}>} The composited image
 */
const compositeOverlayImage = (baseDataUrl, overlayUrl, positioning, maskDimensions) => {
  return new Promise((resolve, reject) => {
    const baseImg = new Image();
    baseImg.crossOrigin = 'anonymous';
    baseImg.onload = () => {
      const overlayImg = new Image();
      overlayImg.crossOrigin = 'anonymous';
      overlayImg.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = baseImg.width;
        canvas.height = baseImg.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(baseImg, 0, 0);

        if (positioning && maskDimensions && maskDimensions.width > 0) {
          const maskScalePercent = positioning.scale / 100;
          const maskAreaWidth = canvas.width * maskScalePercent;
          const maskAspectRatio = maskDimensions.height / maskDimensions.width;
          const maskAreaHeight = maskAreaWidth * maskAspectRatio;

          const maskCenterX = (canvas.width / 100) * positioning.x;
          const maskCenterY = (canvas.height / 100) * positioning.y;

          const drawX = maskCenterX - maskAreaWidth / 2;
          const drawY = maskCenterY - maskAreaHeight / 2;

          ctx.drawImage(overlayImg, drawX, drawY, maskAreaWidth, maskAreaHeight);
        } else {
          ctx.drawImage(overlayImg, 0, 0, canvas.width, canvas.height);
        }

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob from composited canvas'));
            return;
          }
          const reader = new FileReader();
          reader.onloadend = () => resolve({ dataUrl: reader.result, blob });
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        }, 'image/png');
      };
      overlayImg.onerror = () => reject(new Error('Failed to load overlay image'));
      overlayImg.src = overlayUrl;
    };
    baseImg.onerror = () => reject(new Error('Failed to load base image for compositing'));
    baseImg.src = baseDataUrl;
  });
};

/**
 * Checks if a group contains alphanumeric slots that should be debounced.
 * @param {Array<{slotType: string}>} groupSlots - The slots in the group
 * @param {string} visualiserSlotMode - The visualization slot mode
 * @returns {boolean} True if the group contains alphanumeric slots
 */
const hasAlphanumericSlots = (groupSlots, visualiserSlotMode) => {
  return visualiserSlotMode === 'TEXT' || groupSlots.some((slot) => slot.slotType === 'ALPHANUMERIC');
};

/**
 * Validates if visualization should proceed and clears cache if needed.
 * @param {Object} params
 * @param {Object} params.filteredSelections - The filtered selections
 * @param {string} params.personalisationId - The personalisation ID
 * @param {Map} params.cache - The visualization cache
 * @returns {{shouldProceed: boolean, shouldClearCache: boolean}}
 */
const validateVisualisationConditions = ({ filteredSelections, personalisationId, cache, variants }) => {
  const hasAnySelections = filteredSelections?.slots?.some(
    (slot) => slot.selected !== null && slot.selected !== undefined
  );

  if (!hasAnySelections) {
    cache.clear();
    return { shouldProceed: false, shouldClearCache: true };
  }

  if (
    !filteredSelections ||
    VIS_ENABLED !== 'true' ||
    !VITE_IMAGE_CDN_URL ||
    !personalisationId ||
    !PRODUCT_ID ||
    variants?.get(filteredSelections.productVariantId)?.hideFromVisualiser === true
  ) {
    cache.clear();
    return { shouldProceed: false, shouldClearCache: true };
  }

  return { shouldProceed: true, shouldClearCache: false };
};

/**
 * Determines if we're in legacy mode and sets the appropriate visualiser slot mode.
 * @param {Object} params
 * @param {Array} params.slots - The slots array
 * @param {string} params.initialVisualiserSlotMode - The initial visualiser slot mode
 * @returns {{legacyMode: boolean, visualiserSlotMode: string}}
 */
const determineVisualisationMode = ({ slots, initialVisualiserSlotMode }) => {
  const legacyMode = !slots.filter((slot) => slot.visualised === true).some((slot) => slot.visualisationGroup !== null);

  let visualiserSlotMode = initialVisualiserSlotMode;

  if (legacyMode) {
    visualiserSlotMode = slots.some((slot) => slot.slotType === 'IMAGE_UPLOAD') ? 'IMAGE_UPLOAD' : visualiserSlotMode;
  }

  return { legacyMode, visualiserSlotMode };
};

/**
 * Processes and filters selected slots for visualization.
 * @param {Object} params
 * @param {Object} params.filteredSelections - The filtered selections
 * @param {Array<import('../context/personalisation-typedefs').PersonalisationSlot>} params.slots - The full slots array
 * @param {string} params.baseProductConfigurationSlotId - The base product configuration slot ID
 * @param {Map} params.variants - The variants map
 * @returns {Array} The filtered visualization slots
 */
const processVisualisationSlots = ({ filteredSelections, slots, baseProductConfigurationSlotId, variants }) => {
  /** @type {Map<string, import('../context/personalisation-typedefs').PersonalisationSlot>} */
  const slotsMap = new Map(slots.map((slot) => [slot.id, slot]));
  const selectedSlots = filteredSelections.slots
    .filter((slot) => slot.selected)
    .map((selectionSlot) => {
      const fullSlot = slotsMap.get(selectionSlot.id);
      if (fullSlot) {
        return {
          ...fullSlot,
          selected: selectionSlot.selected,
          groupLineItemPropertyKey: selectionSlot.groupLineItemPropertyKey ?? null
        };
      }
      return selectionSlot;
    });

  const afterBaseProductFilter = selectedSlots.filter(({ id }) => id !== baseProductConfigurationSlotId);
  const afterVisualisedFilter = afterBaseProductFilter.filter(({ visualised }) => visualised === true);
  const visSlots = afterVisualisedFilter.filter(({ selected }) => {
    if (selected instanceof File) return true;
    return selected && variants.get(selected)?.hideFromVisualiser !== true;
  });

  return visSlots;
};

/**
 * Handles legacy mode group processing and cache checking.
 * @param {Object} params
 * @param {Array} params.visSlots - The visualization slots
 * @param {string} params.productVariantId - The product variant ID
 * @param {Map} params.cache - The visualization cache
 * @param {Array} params.groupsToProcess - Array to add groups that need processing
 * @param {Array} params.urls - Array to add cached URLs
 * @returns {void}
 */
const handleLegacyModeGroup = ({ visSlots, productVariantId, cache, groupsToProcess, urls }) => {
  const groupId = 'legacy';
  const cacheKey = createGroupCacheKey(groupId, visSlots, productVariantId);

  if (cache.has(cacheKey)) {
    const cachedResult = cache.get(cacheKey);
    if (cachedResult) urls.push(cachedResult);
  } else {
    groupsToProcess.push({
      groupId,
      cacheKey,
      visSlots,
      isLegacy: true
    });
  }
};

/**
 * Groups slots by visualization group.
 * @param {Object} params
 * @param {Array} params.visSlots - The visualization slots
 * @param {Map} params.steps - The steps map
 * @returns {Object} Grouped slots by groupId
 */
const groupSlotsByVisualisationGroup = ({ visSlots, steps }) => {
  return visSlots.reduce(
    (acc, { visualisationGroup, lineItemPropertyKey, stepId, selected, id, slotType, groupLineItemPropertyKey }) => {
      const { groupId, visualisationType, label } = visualisationGroup || {};

      if (!groupId) {
        return acc;
      }
      if (!acc[groupId]) {
        acc[groupId] = {
          visualisationType,
          groupId,
          label,
          mainLineItemPropertyKey: lineItemPropertyKey,
          order: steps.get(stepId)?.orderIndex ?? 0,
          values: []
        };
      }
      acc[groupId].values.push({
        id,
        selected,
        slotType,
        // The active slot-group marker: grouped slots share the same file, so
        // this is what distinguishes which colour option should be visualised
        // (it feeds both the cache key and the active-slot pick for the URL).
        groupLineItemPropertyKey: groupLineItemPropertyKey ?? null
      });

      acc[groupId].values = [...new Set(acc[groupId].values)];
      return acc;
    },
    {}
  );
};

/**
 * Processes grouped slots and checks cache for each group.
 * @param {Object} params
 * @param {Object} params.groupedSlots - The grouped slots
 * @param {string} params.productVariantId - The product variant ID
 * @param {Map} params.cache - The visualization cache
 * @param {Array} params.groupsToProcess - Array to add groups that need processing
 * @param {Array} params.urls - Array to add cached URLs
 * @returns {void}
 */
const processGroupedSlots = ({ groupedSlots, productVariantId, cache, groupsToProcess, urls }) => {
  Object.values(groupedSlots).forEach((groupData, index) => {
    const { groupId, visualisationType, label, values, mainLineItemPropertyKey, order } = groupData;
    const cacheKey = createGroupCacheKey(groupId, values, productVariantId);

    if (cache.has(cacheKey)) {
      const cachedResult = cache.get(cacheKey);
      if (cachedResult) {
        urls.push(cachedResult);
      }
    } else {
      groupsToProcess.push({
        groupId,
        cacheKey,
        visSlots: values,
        visualisationType,
        label,
        mainLineItemPropertyKey,
        order,
        index,
        isLegacy: false
      });
    }
  });
};

/**
 * Separates groups into alphanumeric (debounced) and immediate groups.
 * @param {Object} params
 * @param {Array} params.groupsToProcess - Groups to process
 * @param {string} params.visualiserSlotMode - The visualiser slot mode
 * @returns {{alphanumericGroups: Array, immediateGroups: Array}}
 */
const separateGroupsByType = ({ groupsToProcess, visualiserSlotMode }) => {
  const alphanumericGroups = [];
  const immediateGroups = [];

  groupsToProcess.forEach((groupData) => {
    const { visSlots: groupVisSlots, isLegacy } = groupData;
    const effectiveMode = isLegacy
      ? visualiserSlotMode
      : 'visualisationType' in groupData &&
          groupData.visualisationType &&
          typeof groupData.visualisationType === 'string'
        ? groupData.visualisationType.toUpperCase().replace(' ', '_')
        : visualiserSlotMode;

    if (hasAlphanumericSlots(groupVisSlots, effectiveMode)) {
      alphanumericGroups.push(groupData);
    } else {
      immediateGroups.push(groupData);
    }
  });

  return { alphanumericGroups, immediateGroups };
};

/**
 * Generates a visualization URL for a single group.
 * @param {Object} params
 * @param {Object} params.groupData - The group data
 * @param {Object} params.filteredSelections - The filtered selections
 * @param {string} params.personalisationId - The personalisation ID
 * @param {string} params.visualiserSlotMode - The visualiser slot mode
 * @param {string} params.baseProductConfigurationSlotId - The base product configuration slot ID
 * @param {Map<string, import('../context/personalisation-typedefs').PersonalisationSlot> | null} params.slotsMap - Map of slots for quick lookup
 * @returns {Promise<Object|null>} The generated URL result
 */
const generateGroupUrl = async ({
  groupData,
  filteredSelections,
  personalisationId,
  visualiserSlotMode,
  baseProductConfigurationSlotId,
  slotsMap
}) => {
  const { groupId, visSlots: groupVisSlots, isLegacy } = groupData;
  let result;

  if (isLegacy) {
    result = await handleSingleVisualisationUrl({
      visSlots: groupVisSlots,
      filteredSelections,
      personalisationId,
      visualiserSlotMode,
      baseProductConfigurationSlotId,
      groupId: 'legacy',
      slotsMap
    });
  } else {
    if ('visualisationType' in groupData && 'label' in groupData) {
      const { visualisationType, label, mainLineItemPropertyKey, order, index } = groupData;
      result = await handleSingleVisualisationUrl({
        visSlots: groupVisSlots,
        filteredSelections,
        personalisationId,
        visualiserSlotMode: visualisationType?.toUpperCase().replace(' ', '_'),
        baseProductConfigurationSlotId,
        groupId,
        lineItemPropertyKey: mainLineItemPropertyKey
          ? `${mainLineItemPropertyKey} - Visual`
          : `_featured_image_${index}`,
        label,
        order,
        slotsMap
      });
    }
  }

  return result;
};

/**
 * Processes immediate groups (non-alphanumeric).
 * @param {Object} params
 * @param {Array} params.immediateGroups - Groups to process immediately
 * @param {Object} params.filteredSelections - The filtered selections
 * @param {string} params.personalisationId - The personalisation ID
 * @param {string} params.visualiserSlotMode - The visualiser slot mode
 * @param {string} params.baseProductConfigurationSlotId - The base product configuration slot ID
 * @param {Map} params.cache - The visualization cache
 * @param {Map<string, import('../context/personalisation-typedefs').PersonalisationSlot> | null} params.slotsMap - Map of slots for quick lookup
 * @returns {Promise<Array>} Generated URLs
 */
const processImmediateGroups = async ({
  immediateGroups,
  filteredSelections,
  personalisationId,
  visualiserSlotMode,
  baseProductConfigurationSlotId,
  cache,
  slotsMap
}) => {
  const immediateUrls = await Promise.all(
    immediateGroups.map(async (groupData) => {
      const { cacheKey } = groupData;
      const result = await generateGroupUrl({
        groupData,
        filteredSelections,
        personalisationId,
        visualiserSlotMode,
        baseProductConfigurationSlotId,
        slotsMap
      });

      if (result && result !== null && typeof result === 'object') {
        cache.set(cacheKey, result);
        return result;
      }

      return null;
    })
  );

  return immediateUrls.filter((url) => url !== null && typeof url === 'object');
};

/**
 * Creates a debounced executor for a cache key.
 * @param {Object} params
 * @param {string} params.cacheKey - The cache key
 * @param {Object} params.filteredSelections - The filtered selections
 * @param {string} params.personalisationId - The personalisation ID
 * @param {string} params.visualiserSlotMode - The visualiser slot mode
 * @param {string} params.baseProductConfigurationSlotId - The base product configuration slot ID
 * @param {Map} params.cache - The visualization cache
 * @param {Map<string, import('../context/personalisation-typedefs').PersonalisationSlot> | null} params.slotsMap - Map of slots for quick lookup
 * @returns {Object} The debounced executor
 */
const createDebouncedExecutor = ({ personalisationId, visualiserSlotMode, baseProductConfigurationSlotId, cache }) => {
  let latestGroupData = null;
  let latestFilteredSelections = null;
  let latestCacheKey = null;
  let pendingResolves = [];
  let abortController = null;

  const debouncedFn = debounce(async () => {
    const currentGroupData = latestGroupData;
    const currentFilteredSelections = latestFilteredSelections;
    const currentCacheKey = latestCacheKey;
    const resolvesToCall = [...pendingResolves];
    pendingResolves = [];

    if (!currentGroupData || resolvesToCall.length === 0) return;

    // Abort any previous request
    if (abortController) {
      abortController.abort();
    }

    // Create new AbortController for this request
    abortController = new AbortController();
    const signal = abortController.signal;

    const { groupId: currentGroupId, visSlots: currentVisSlots, isLegacy: currentIsLegacy } = currentGroupData;
    let result;

    try {
      const currentSlotsMap = currentGroupData?.slotsMap;

      if (currentIsLegacy) {
        result = await handleSingleVisualisationUrl({
          visSlots: currentVisSlots,
          filteredSelections: currentFilteredSelections,
          personalisationId,
          visualiserSlotMode,
          baseProductConfigurationSlotId,
          groupId: 'legacy',
          signal,
          slotsMap: currentSlotsMap
        });
      } else {
        if ('visualisationType' in currentGroupData && 'label' in currentGroupData) {
          const { visualisationType, label, mainLineItemPropertyKey, order, index } = currentGroupData;
          result = await handleSingleVisualisationUrl({
            visSlots: currentVisSlots,
            filteredSelections: currentFilteredSelections,
            personalisationId,
            visualiserSlotMode: visualisationType?.toUpperCase().replace(' ', '_'),
            baseProductConfigurationSlotId,
            groupId: currentGroupId,
            lineItemPropertyKey: mainLineItemPropertyKey
              ? `${mainLineItemPropertyKey} - Visual`
              : `_featured_image_${index}`,
            label,
            order,
            signal,
            slotsMap: currentSlotsMap
          });
        }
      }

      // Only cache and resolve if not aborted
      if (!signal.aborted && result && result !== null && typeof result === 'object') {
        if (currentCacheKey) {
          cache.set(currentCacheKey, result);
        }
        resolvesToCall.forEach((resolve) => resolve(result));
      } else {
        // Resolve with null if aborted or no result, to prevent Promise.all from hanging
        resolvesToCall.forEach((resolve) => resolve(null));
      }
    } catch (error) {
      // For abort errors and other errors, resolve with null to prevent Promise.all from hanging
      resolvesToCall.forEach((resolve) => resolve(null));
      if (error.name !== 'AbortError') {
        console.error('Visualisation URL generation error:', error);
      }
    }
  }, 500);

  return {
    fn: debouncedFn,
    update: (newGroupData, newResolve, newFilteredSelections, newCacheKey) => {
      pendingResolves.push(newResolve);
      latestGroupData = newGroupData;
      latestFilteredSelections = newFilteredSelections;
      latestCacheKey = newCacheKey;
      debouncedFn();
    }
  };
};

/**
 * Processes alphanumeric groups with debouncing.
 * @param {Object} params
 * @param {Array} params.alphanumericGroups - Groups to process with debouncing
 * @param {Object} params.filteredSelections - The filtered selections
 * @param {string} params.personalisationId - The personalisation ID
 * @param {string} params.visualiserSlotMode - The visualiser slot mode
 * @param {string} params.baseProductConfigurationSlotId - The base product configuration slot ID
 * @param {Map} params.cache - The visualization cache
 * @param {Object} params.debouncedGeneratorsRef - The debounced generators ref object
 * @param {Map<string, import('../context/personalisation-typedefs').PersonalisationSlot> | null} params.slotsMap - Map of slots for quick lookup
 * @returns {Promise<Array>} Generated URLs
 */
const processDebouncedGroups = async ({
  alphanumericGroups,
  filteredSelections,
  personalisationId,
  visualiserSlotMode,
  baseProductConfigurationSlotId,
  cache,
  debouncedGeneratorsRef,
  slotsMap
}) => {
  const debouncedUrls = await Promise.all(
    alphanumericGroups.map((groupData) => {
      const { groupId, cacheKey } = groupData;

      return new Promise((resolve) => {
        const debouncedGeneratorsMap = debouncedGeneratorsRef.current;
        // Use groupId (stable across keystrokes) instead of cacheKey (changes with text)
        let debouncedExecutor = debouncedGeneratorsMap.get(groupId);

        if (!debouncedExecutor) {
          debouncedExecutor = createDebouncedExecutor({
            personalisationId,
            visualiserSlotMode,
            baseProductConfigurationSlotId,
            cache
          });

          debouncedGeneratorsMap.set(groupId, debouncedExecutor);
        }

        // Pass filteredSelections and cacheKey so the executor always uses fresh data
        debouncedExecutor.update({ ...groupData, slotsMap }, resolve, filteredSelections, cacheKey);
      });
    })
  );

  return debouncedUrls.filter((url) => url !== null && typeof url === 'object');
};

/**
 * Sorts and filters the final URLs array.
 * @param {Array} urls - The URLs array
 * @returns {Array} Sorted and filtered URLs
 */
const sortAndFilterUrls = (urls) => {
  return urls
    .filter((url) => url !== null && typeof url === 'object')
    .sort((a, b) => {
      const orderA = 'order' in a && typeof a.order === 'number' ? a.order : 0;
      const orderB = 'order' in b && typeof b.order === 'number' ? b.order : 0;
      return orderA - orderB;
    });
};

/**
 * @param {Object} params
 * @param {{filteredSelections: import('../context/personalisation-typedefs').Selections, personalisationId: string, visualiserSlotMode: string, variants: Map<string, import('../context/personalisation-typedefs').ProductVariant>}} params.deps
 * @param {string} params.baseProductConfigurationSlotId
 * @param {Array<import('../context/personalisation-typedefs').PersonalisationSlot>} params.slots
 * @param {Map<string, import("../context/personalisation-typedefs").PersonalisationStep>} params.steps
 * @returns {{ visualisationUrls: { url: string, visualiserSlotMode: string, groupId: string, file: File | null, lineItemPropertyKey: string, label: string, order: number }[] }}
 */
export const useVisualisationUrlManagement = ({ deps, baseProductConfigurationSlotId, slots, steps }) => {
  // Use useRef to store the cache across renders (following React rules)
  const visualizationCacheRef = useRef(new Map());
  // Use useRef to store debounced functions for alphanumeric groups
  const debouncedGeneratorsRef = useRef(new Map());

  const { filteredSelections, personalisationId, visualiserSlotMode: initialVisualiserSlotMode, variants } = deps;

  const urlsItems = useMemo(() => {
    return new Promise((resolve) => {
      const cache = visualizationCacheRef.current;

      // Validate conditions and early return if needed
      const validation = validateVisualisationConditions({
        filteredSelections,
        personalisationId,
        cache,
        variants
      });

      if (!validation.shouldProceed) {
        resolve([]);
        return;
      }

      // Determine visualization mode
      const { legacyMode, visualiserSlotMode } = determineVisualisationMode({
        slots,
        initialVisualiserSlotMode
      });

      // Process and filter visualization slots
      const visSlots = processVisualisationSlots({
        filteredSelections,
        slots,
        baseProductConfigurationSlotId,
        variants
      });

      // Create a map of slots for quick lookup of full slot data (including positioning)
      const slotsMapForLookup = new Map(slots.map((slot) => [slot.id, slot]));

      const { productVariantId } = filteredSelections;

      (async () => {
        try {
          /** @type {{ url: string, visualiserSlotMode: string, groupId: string }[]} */
          let urls = [];
          const groupsToProcess = [];

          // Handle legacy mode or grouped mode
          if (legacyMode) {
            handleLegacyModeGroup({
              visSlots,
              productVariantId,
              cache,
              groupsToProcess,
              urls
            });
          } else {
            // Group slots by visualization group
            const groupedSlots = groupSlotsByVisualisationGroup({
              visSlots,
              steps
            });

            // Process grouped slots and check cache
            processGroupedSlots({
              groupedSlots,
              productVariantId,
              cache,
              groupsToProcess,
              urls
            });
          }

          // Generate URLs for groups that aren't cached
          if (groupsToProcess.length > 0) {
            // Separate groups into alphanumeric (debounced) and immediate groups
            const { alphanumericGroups, immediateGroups } = separateGroupsByType({
              groupsToProcess,
              visualiserSlotMode
            });

            // Process immediate groups right away
            if (immediateGroups.length > 0) {
              const immediateUrls = await processImmediateGroups({
                immediateGroups,
                filteredSelections,
                personalisationId,
                visualiserSlotMode,
                baseProductConfigurationSlotId,
                cache,
                slotsMap: slotsMapForLookup
              });

              urls.push(...immediateUrls);
            }

            // Process alphanumeric groups with debouncing
            if (alphanumericGroups.length > 0) {
              const debouncedUrls = await processDebouncedGroups({
                alphanumericGroups,
                filteredSelections,
                personalisationId,
                visualiserSlotMode,
                baseProductConfigurationSlotId,
                cache,
                debouncedGeneratorsRef,
                slotsMap: slotsMapForLookup
              });

              urls.push(...debouncedUrls);
            }
          }

          // Sort and filter final URLs
          urls = sortAndFilterUrls(urls);
          resolve(urls);
        } catch (error) {
          console.error('Visualization URL generation failed:', error);
          resolve([]);
        }
      })();
    });
  }, [
    baseProductConfigurationSlotId,
    steps,
    filteredSelections,
    personalisationId,
    initialVisualiserSlotMode,
    variants,
    slots
  ]);

  return {
    visualisationUrls: urlsItems
  };
};

/**
 * @param {Object} params
 * @param {Array} params.visSlots
 * @param {Object} params.filteredSelections
 * @param {string} params.personalisationId
 * @param {string} params.visualiserSlotMode
 * @param {string} params.baseProductConfigurationSlotId
 * @param {string} [params.groupId='legacy']
 * @param {string} [params.lineItemPropertyKey='_featured_image']
 * @param {string} [params.label='None']
 * @param {number} [params.order=0]
 * @param {AbortSignal | null} [params.signal] - Abort signal for cancelling the request
 * @param {Map<string, import('../context/personalisation-typedefs').PersonalisationSlot> | null} [params.slotsMap] - Map of slots for quick lookup
 */
const handleSingleVisualisationUrl = async ({
  visSlots,
  filteredSelections,
  personalisationId,
  visualiserSlotMode,
  baseProductConfigurationSlotId,
  groupId = 'legacy',
  lineItemPropertyKey = '_featured_image',
  label = 'None',
  order = 0,
  signal = null,
  slotsMap = null
}) => {
  const { productVariantId, slots } = filteredSelections;

  if (!VITE_IMAGE_CDN_URL || !personalisationId || !slots || !PRODUCT_ID) {
    return null;
  }

  // Check if already aborted
  if (signal?.aborted) {
    return null;
  }
  const baseProductSlot = slots.find((slot) => slot.id === baseProductConfigurationSlotId);
  const imageFileSlotPresent = slots.find((slot) => slot.slotType === 'IMAGE_UPLOAD');

  // Sequential mode needs at least one slot selected.
  if (visualiserSlotMode === 'SEQUENTIAL' && visSlots.length === 0) {
    return null;
  }
  // Fixed mode needs at least the base product slot selected.
  if (visualiserSlotMode === 'FIXED' && !baseProductSlot?.selected && !imageFileSlotPresent) {
    return null;
  }
  const url = new URL(VITE_IMAGE_CDN_URL + `/${removeGid({ id: `${PRODUCT_ID}` })}.${imageType}`);
  url.searchParams.set('layoutId', removeGid({ id: `${personalisationId}` }));
  url.searchParams.set('variant', removeGid({ id: `${productVariantId}` }));
  if (groupId !== 'legacy') url.searchParams.set('layoutGroup', groupId);

  // Add all slot params
  visSlots
    .filter((slot) => slot.selected)
    .forEach((slot, index) => {
      // File selections are POSTed in the image-upload block below — never stringify as [object File].
      if (slot.selected instanceof File) {
        return;
      }

      let slotValue = removeGid({ id: `${slot.selected}` });
      const cleanSlotId = removeGid({ id: slot.id });
      let slotId = `slot${cleanSlotId}`;

      if (
        ['FIXED', 'SEQUENTIAL'].includes(visualiserSlotMode) &&
        typeof slot.selected !== 'string' &&
        !/^\d+$/.test(slot.selected)
      ) {
        // If we're in fixed or sequential mode and the slot value is not a string, we need to bail out.
        return;
      }

      // If we're in sequential mode, we need to add the index to the slot id
      // so we can ensure the order of the slots through query params.
      if (visualiserSlotMode === 'SEQUENTIAL') {
        slotId = `slot${cleanSlotId}.${index}`;
      }

      if (visualiserSlotMode === 'TEXT') {
        slotValue = btoa(
          JSON.stringify({
            text: slot.selected.text,
            fontName: slot.selected.font
          })
        );
      }

      return url.searchParams.set(slotId, slotValue);
    });
  let result = url.toString();

  // Check if we have an IMAGE_UPLOAD slot with a File selected
  // This can happen either when visualiserSlotMode is IMAGE_UPLOAD, or when a slot has slotType IMAGE_UPLOAD
  // (e.g., fingerprint slots in a Fixed visualisation group)
  const hasImageUploadSlot = visSlots.some((slot) => slot.slotType === 'IMAGE_UPLOAD' && slot.selected instanceof File);
  const isImageUploadMode = visualiserSlotMode === 'IMAGE_UPLOAD';

  if ((isImageUploadMode || hasImageUploadSlot) && visSlots.some((slot) => slot.selected instanceof File)) {
    // The selected value should be the image file for the mask/user content.
    const imageFileSlots = visSlots.filter((slot) => slot.selected instanceof File);
    const imageFileSlot =
      imageFileSlots.find((slot) => slot.groupLineItemPropertyKey) ??
      imageFileSlots.find((slot) => {
        const selection = filteredSelections.slots?.find((s) => s.id === slot.id);
        return selection?.groupLineItemPropertyKey;
      }) ??
      imageFileSlots[0];
    const imageFile = imageFileSlot?.selected;

    // Look up full slot data from slotsMap to get positioning information
    const fullSlotData = slotsMap?.get(imageFileSlot?.id);
    const positionX = fullSlotData?.positionX ?? imageFileSlot?.positionX;
    const positionY = fullSlotData?.positionY ?? imageFileSlot?.positionY;
    const scale = fullSlotData?.scale ?? imageFileSlot?.scale;

    // If we're not processing an image upload then bail out with just the url.
    if (!imageFile) return { url: result, visualiserSlotMode, groupId, label, lineItemPropertyKey };
    const dataUrl = await fileToDataURL(imageFile);
    const base64Payload = (dataUrl && dataUrl.toString().split(',')[1]) || dataUrl || '';
    // Remove the slot value as its a file - then replace with placeholder.
    const imageSlotParam = `slot${removeGid({ id: imageFileSlot.id })}`;
    url.searchParams.delete(imageSlotParam);
    url.searchParams.set(imageSlotParam, 'image-upload');
    url.searchParams.set('w', '1000');

    const imageUploadUrl = url.toString();

    const uploadBody = JSON.stringify({
      [`image_${removeGid({ id: imageFileSlot.id })}`]: base64Payload
    });
    // CloudFront signs origin requests to the visualiser Lambda with OAC (SigV4); for
    // POSTs the client must supply the hex SHA-256 of the body or the origin returns 403.
    const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(uploadBody));
    const bodySha256 = Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    const { localUrl, file } = await fetch(imageUploadUrl, {
      method: 'POST',
      headers: { 'x-amz-content-sha256': bodySha256 },
      body: uploadBody,
      signal
    })
      .then((res) => {
        if (signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }
        return res.blob();
      })
      .then((blob) => {
        if (signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }
        return new Promise((resolve, reject) => {
          // Reject if aborted while setting up FileReader
          if (signal?.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
            return;
          }

          const reader = new FileReader();

          // Handle abort during read
          if (signal) {
            signal.addEventListener('abort', () => {
              reader.abort();
              reject(new DOMException('Aborted', 'AbortError'));
            });
          }

          reader.onloadend = () => {
            if (signal?.aborted) {
              reject(new DOMException('Aborted', 'AbortError'));
              return;
            }
            resolve({ localUrl: reader.result, file: blob });
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
      })
      .then(({ localUrl, file }) => {
        if (signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }
        return { localUrl, file };
      });

    // Composite overlay image on top of visualised output if one exists,
    // positioned within the mask bounding box
    const overlayUrl = fullSlotData?.overlayImage?.url;
    let finalUrl = localUrl;
    let finalFile = file;

    if (overlayUrl) {
      try {
        const maskImg = fullSlotData?.targetImagePlaceholder;
        const overlayPositioning =
          positionX != null && positionY != null && scale != null
            ? { x: Number(positionX), y: Number(positionY), scale: Number(scale) }
            : null;
        const maskDimensions =
          maskImg?.width && maskImg?.height ? { width: Number(maskImg.width), height: Number(maskImg.height) } : null;

        const { dataUrl: compositedDataUrl, blob: compositedBlob } = await compositeOverlayImage(
          localUrl,
          overlayUrl,
          overlayPositioning,
          maskDimensions
        );
        finalUrl = compositedDataUrl;
        finalFile = compositedBlob;
      } catch (err) {
        console.error('Failed to composite overlay image, using base visualised image:', err);
      }
    }

    // Return the file blob and the local URL.
    // Include positioning data for potential image cropping in line item properties
    return {
      url: finalUrl,
      file: finalFile,
      visualiserSlotMode,
      groupId,
      lineItemPropertyKey,
      label,
      order,
      // Add positioning data if available for image cropping
      positioning:
        positionX != null && positionY != null && scale != null
          ? {
              x: positionX,
              y: positionY,
              scale: scale
            }
          : null
    };
  }

  // If we're not processing an image upload then bail out with just the url.
  const returnValue = {
    url: result,
    // Find the file from the selected slots.
    file: visSlots.find((slot) => slot.selected instanceof File)?.selected || null,
    visualiserSlotMode,
    groupId,
    lineItemPropertyKey,
    label,
    order
  };
  return returnValue;
};

// Export pure helper functions for testing
export { processVisualisationSlots, groupSlotsByVisualisationGroup, determineVisualisationMode };
